<template>
  <div class="wrapper">
    <!-- Page header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol sm="7">
            <h1 class="h4 mb-1 font-weight-bold">My claims</h1>
            <p class="text-muted mb-0">Claim back money you spent for work</p>
          </CCol>
          <CCol sm="5" class="d-flex justify-content-sm-end mt-3 mt-sm-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshTable">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add claim
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard class="border-0">
      <!-- First load -->
      <CCardBody v-if="loading && !items.length" class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3 mb-0">Loading your claims…</p>
      </CCardBody>

      <!-- Error -->
      <CCardBody v-else-if="loadError && !items.length" class="text-center py-5">
        <CIcon name="cil-warning" size="3xl" class="mb-3 text-danger" />
        <h2 class="h5">Couldn't load your claims</h2>
        <p class="text-muted mb-3">Check your connection and try again.</p>
        <CButton color="primary" @click="refreshTable">
          <CIcon name="cil-reload" /> Try again
        </CButton>
      </CCardBody>

      <!-- Empty -->
      <CCardBody v-else-if="!items.length" class="text-center py-5">
        <CIcon name="cil-description" size="3xl" class="mb-3 text-muted" />
        <h2 class="h5">No claims yet</h2>
        <p class="text-muted mb-3">Add a claim with your receipts and send it to your employer for approval.</p>
        <CButton color="primary" @click="addNew">
          <CIcon name="cil-plus" /> Add claim
        </CButton>
      </CCardBody>

      <template v-else>
        <CProgress v-if="loading" :value="100" animated color="primary" size="xs" />
        <!-- Desktop table -->
        <CCardBody class="d-none d-lg-block">
          <CDataTable
            :items="rows"
            :fields="fields"
            items-per-page-select
            :items-per-page="25"
            hover
            sorter
            :sorter-value="{ column: 'date', asc: false }"
            pagination
            responsive
          >
            <template #claimNo="{ item }">
              <td class="py-2 text-monospace">
                <router-link :to="`/employee/Claim/${item.id}`">{{ item.claimNo || "—" }}</router-link>
              </td>
            </template>
            <template #date="{ item }">
              <td class="py-2">{{ formatDate(item.date) }}</td>
            </template>
            <template #totalAmount="{ item }">
              <td class="py-2 text-right font-weight-bold">{{ money(item.totalAmount) }}</td>
            </template>
            <template #claimStateDescription="{ item }">
              <td class="py-2">
                <CBadge :color="hrStatusColor('claim', item.claimStateDescription)">
                  {{ hrStatusLabel('claim', item.claimStateDescription) }}
                </CBadge>
              </td>
            </template>
            <template #actions="{ item }">
              <td class="py-2 text-right text-nowrap">
                <CButton color="light" size="sm" class="border" :aria-label="`Open claim ${item.claimNo || ''}`" title="Open" @click="onEdit(item)">
                  <CIcon name="cil-pencil" />
                </CButton>
                <CButton
                  v-if="canDelete(item)"
                  color="light"
                  size="sm"
                  class="border text-danger ml-1"
                  :aria-label="`Delete claim ${item.claimNo || ''}`"
                  title="Delete"
                  @click="showDeleteConfirmation(item)"
                >
                  <CIcon name="cil-trash" />
                </CButton>
              </td>
            </template>
          </CDataTable>
        </CCardBody>

        <!-- Phone / tablet cards -->
        <CCardBody class="d-lg-none p-2">
          <div v-for="item in sortedRows" :key="item.id" class="border rounded p-3 mb-2">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <router-link :to="`/employee/Claim/${item.id}`" class="font-weight-bold text-monospace">
                  {{ item.claimNo || "Claim" }}
                </router-link>
                <small class="d-block text-muted">{{ formatDate(item.date) }} · {{ item.businessName }}</small>
              </div>
              <CBadge :color="hrStatusColor('claim', item.claimStateDescription)">
                {{ hrStatusLabel('claim', item.claimStateDescription) }}
              </CBadge>
            </div>
            <div class="d-flex justify-content-between align-items-center mt-2">
              <span class="font-weight-bold">{{ money(item.totalAmount) }}</span>
              <CDropdown color="light" placement="bottom-end">
                <template #toggler-content>
                  <CIcon name="cil-options" />
                  <span class="sr-only">More actions</span>
                </template>
                <CDropdownItem @click="onEdit(item)">
                  <CIcon name="cil-pencil" class="mr-2" /> Open
                </CDropdownItem>
                <template v-if="canDelete(item)">
                  <CDropdownDivider />
                  <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                    <CIcon name="cil-trash" class="mr-2" /> Delete
                  </CDropdownItem>
                </template>
              </CDropdown>
            </div>
          </div>
        </CCardBody>
      </template>
    </CCard>

    <CModal
      :title="`Delete claim ${itemToDelete.claimNo || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        Your claim dated {{ formatDate(itemToDelete.date) }} for {{ money(itemToDelete.totalAmount) }}
        and its receipts will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" /> Delete claim
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ClaimApi from "@/lib/claimApi";
import { formatDate, asDateString } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { hrStatusMixin } from "@/utils/hrStatusBadges";

const fields = [
  { key: "claimNo", label: "Claim no." },
  { key: "date", label: "Date" },
  { key: "businessName", label: "Employer" },
  { key: "totalAmount", label: "Amount", _style: "text-align:right" },
  { key: "claimStateDescription", label: "Status" },
  { key: "actions", label: "", _style: "width:1%", sorter: false, filter: false },
];

export default {
  name: "ClaimList",
  mixins: [hrStatusMixin],
  data() {
    return {
      loading: true,
      loadError: false,
      items: [],
      fields,
      api: new ClaimApi(),
      warningModal: false,
      deleting: false,
      itemToDelete: {},
    };
  },
  computed: {
    rows() {
      return this.items.map((item) => ({
        ...item,
        profileName: (item.profile && item.profile.fullName) || "",
        businessName: (item.business && item.business.name) || "",
        totalAmount: this.getTotalAmount(item),
      }));
    },
    sortedRows() {
      return [...this.rows].sort((a, b) =>
        asDateString(b.date).localeCompare(asDateString(a.date))
      );
    },
  },
  mounted() {
    this.$store.commit("setPageNavItems", []);
    this.refreshTable();
  },
  methods: {
    formatDate,
    money(v) {
      return `RM ${formatMyr(v)}`;
    },
    getTotalAmount(item) {
      return (item.items || []).reduce((sum, claim) => sum + (parseFloat(claim.amount) || 0), 0);
    },
    // Only drafts can be deleted; submitted or settled claims stay on record.
    canDelete(item) {
      return item.claimStateDescription === "Draft";
    },
    refreshTable() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getListByCurrentUser()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error("Couldn't load claims", err);
          this.loadError = true;
          if (this.items.length) {
            this.$toast.error("Couldn't refresh your claims. Check your connection and try again.");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/employee/Claim/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Claim deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
        })
        .catch(({ data }) => {
          this.$toast.error(`Couldn't delete the claim. ${helper.getErrorMessage(data)}`);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/employee/Claim" });
    },
  },
};
</script>
